import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inputCls, primaryBtn } from "@/components/AuthShell";
import LiveVerificationGate from "@/components/LiveVerificationGate";
import {
  ShieldCheck, Phone, MapPin, Car, Camera, Users, BadgeCheck, Stethoscope,
  FileCheck2, Check, ChevronLeft, Plus, Trash2, Upload, Loader2, IdCard,
} from "lucide-react";

const STEPS = [
  { key: "safety", label: "Safety", icon: ShieldCheck },
  { key: "driver", label: "Driver", icon: Car },
  { key: "evidence", label: "Evidence", icon: Camera },
  { key: "witness", label: "Witnesses", icon: Users },
  { key: "police", label: "Police", icon: BadgeCheck },
  { key: "medical", label: "Medical", icon: Stethoscope },
  { key: "insurance", label: "Insurance", icon: FileCheck2 },
];

const FOLDERS = ["Vehicle Damage", "Other Vehicle", "Other Driver's Info", "Other Driver's Insurance Card", "Other Driver's License", "Roadway", "Injuries", "Witnesses", "Police"];

const DAMAGE_OPTIONS = ["Rear", "Front", "Driver Side", "Passenger Side", "Front Left", "Front Right", "Rear Left", "Rear Right", "Multiple Areas", "Total Loss", "Other"];
const MEDICAL_TYPES = ["Ambulance", "ER", "Urgent Care", "Chiropractor", "Physical Therapy", "MRI", "Surgery", "Pain Journal"];

// Map the saved Driver Profile onto the case's driver object.
const profileToDriver = (p) => ({
  name: p.full_legal_name || "",
  phone: p.phone || "",
  email: p.email || "",
  insurance_company: p.insurance_company || "",
  policy_number: p.policy_number || "",
  license: p.dl_number || "",
  dl_state: p.dl_state || "",
  license_plate: p.license_plate || "",
  vehicle: [p.vehicle_year, p.vehicle_make, p.vehicle_model].filter(Boolean).join(" "),
});

// Reverse-map the case driver back onto the profile (only when the client opts in).
const driverToProfilePatch = (d) => {
  const [year, make, ...model] = (d.vehicle || "").trim().split(/\s+/);
  const yearIsNum = /^\d{2,4}$/.test(year || "");
  return {
    full_legal_name: d.name || "",
    phone: d.phone || "",
    email: d.email || "",
    insurance_company: d.insurance_company || "",
    policy_number: d.policy_number || "",
    dl_number: d.license || "",
    dl_state: d.dl_state || "",
    license_plate: d.license_plate || "",
    vehicle_year: yearIsNum ? year : "",
    vehicle_make: yearIsNum ? (make || "") : (year || ""),
    vehicle_model: yearIsNum ? model.join(" ") : [make, ...model].filter(Boolean).join(" "),
  };
};

const Field = ({ label, ...props }) => (
  <div>
    <label className="text-sm font-medium text-slate-700">{label}</label>
    <input className={`${inputCls} mt-1.5`} {...props} />
  </div>
);

export default function NewAccident() {
  const [params] = useSearchParams();
  const caseId = params.get("case");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [caseData, setCaseData] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  // local form state
  const [safety, setSafety] = useState({ safe: null });
  const [location, setLocation] = useState({});
  const [damageLocation, setDamageLocation] = useState("");
  const [struckByOther, setStruckByOther] = useState(null);
  const [driver, setDriver] = useState({});
  const [witnesses, setWitnesses] = useState([]);
  const [police, setPolice] = useState({});
  const [medical, setMedical] = useState([]);
  const [insurance, setInsurance] = useState({});
  const [evidence, setEvidence] = useState([]);
  const [folder, setFolder] = useState(FOLDERS[0]);
  const [uploading, setUploading] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const [updateProfileToo, setUpdateProfileToo] = useState(false);
  const [savedProfile, setSavedProfile] = useState(null);
  const [verif, setVerif] = useState(null);
  const [attorneyConsent, setAttorneyConsent] = useState(false);
  const liveRef = useRef();

  const loadVerif = () =>
    api.get(`/cases/${caseId}/verification`).then(({ data }) => setVerif(data)).catch(() => setVerif({ legacy: true }));

  useEffect(() => {
    if (!caseId) { navigate("/dashboard"); return; }
    loadVerif();
    (async () => {
      try {
        const { data } = await api.get(`/cases/${caseId}`);
        setCaseData(data);
        setSafety(data.safety || { safe: null });
        setLocation(data.location || {});
        setDamageLocation(data.damage_location || "");
        setStruckByOther(typeof data.struck_by_other_vehicle === "boolean" ? data.struck_by_other_vehicle : null);
        setWitnesses(data.witnesses || []);
        setPolice(data.police || {});
        setMedical(data.medical || []);
        setInsurance(data.insurance || {});
        setEvidence(data.evidence || []);
        // Load the saved Driver Profile once so returning clients can re-apply it
        // anytime via the "Use My Saved Profile" chip.
        let p = null;
        try { const r = await api.get("/driver-profile"); p = r.data; setSavedProfile(r.data); } catch { /* none yet */ }
        // Auto-fill only for a FRESH report (empty driver). A report copies profile
        // data at creation time; later edits to report or profile never rewrite each other.
        const existingDriver = data.driver || {};
        if (Object.keys(existingDriver).length === 0) {
          if (p && p.filled > 0) { setDriver(profileToDriver(p)); setAutoFilled(true); }
        } else {
          setDriver(existingDriver);
        }
      } catch {
        toast.error("Could not load case");
        navigate("/dashboard");
      }
    })();
  }, [caseId, navigate]);

  const captureLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, captured_at: new Date().toISOString() });
        toast.success("Location captured");
      },
      () => toast.error("Could not get location — please allow access"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (step === 0 && caseData && !location.captured_at && navigator.geolocation) {
      captureLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, caseData]);

  const saveSection = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/cases/${caseId}`, payload);
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const maybeUpdateProfile = async () => {
    if (!updateProfileToo) return;
    try { await api.put("/driver-profile", driverToProfilePatch(driver)); }
    catch { /* non-blocking */ }
  };

  const applySavedProfile = () => {
    if (!savedProfile) return;
    setDriver(profileToDriver(savedProfile));
    setAutoFilled(true);
    toast.success("Saved profile applied");
  };

  const next = async () => {
    if (step === 0 && !damageLocation) { toast.error("Please select where your vehicle was damaged."); return; }
    const map = [
      { safety, location, damage_location: damageLocation, struck_by_other_vehicle: struckByOther }, { driver }, {}, { witnesses }, { police }, { medical }, { insurance },
    ][step];
    if (Object.keys(map).length) {
      try { await api.put(`/cases/${caseId}`, map); }
      catch { toast.error("Couldn't save — we'll retry when you finish."); }
    }
    if (step === 1) await maybeUpdateProfile();
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const finish = async (withConsent = true) => {
    if (saving) return; // duplicate-press guard
    setSaving(true);
    try {
      // Save the full case in one request so nothing is lost, even if an
      // individual step save failed earlier due to a flaky connection.
      await api.put(`/cases/${caseId}`, { safety, location, damage_location: damageLocation, struck_by_other_vehicle: struckByOther, driver, witnesses, police, medical, insurance });
      await maybeUpdateProfile();
      await api.post(`/cases/${caseId}/submit`, { attorney_consent: withConsent });
      if (withConsent) {
        navigate(`/cases/${caseId}/submitted`);
      } else {
        toast.success("Case saved. It was not submitted for attorney review.");
        navigate(`/cases/${caseId}`);
      }
    } catch {
      toast.error("Could not submit. Please check your connection and try again.");
    } finally { setSaving(false); }
  };

  const handleUpload = async (e, source = "gallery") => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let lat, lng;
    if (navigator.geolocation) {
      await new Promise((res) => navigator.geolocation.getCurrentPosition(
        (p) => { lat = p.coords.latitude; lng = p.coords.longitude; res(); }, () => res(), { timeout: 5000 }
      ));
    }
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", folder);
      fd.append("capture_source", source);
      if (lat) fd.append("lat", String(lat));
      if (lng) fd.append("lng", String(lng));
      try {
        const { data } = await api.post(`/cases/${caseId}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEvidence((prev) => [data, ...prev]);
      } catch (err) {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : `Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (liveRef.current) liveRef.current.value = "";
    toast.success("Evidence uploaded");
  };

  const removeEvidence = async (id) => {
    try { await api.delete(`/evidence/${id}`); setEvidence((p) => p.filter((e) => e.evidence_id !== id)); } catch { /* ignore */ }
  };

  if (!caseData) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  const manualPending = verif?.stages?.find((s) => s.key === "gps")?.manual_review?.status === "pending";
  const gateDone = !verif || verif.legacy ||
    (verif.safety && verif.phone_verified && (verif.gps_verified || manualPending));

  const StepIcon = STEPS[step].icon;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-navy" data-testid="wizard-exit">
            <ChevronLeft className="w-4 h-4" /> Exit
          </button>
          <span className="font-heading font-bold text-navy">New Accident Report</span>
          <a href="tel:911" className="flex items-center gap-1.5 text-sm font-semibold text-red-500"><Phone className="w-4 h-4" /> 911</a>
        </div>
        {/* Stepper */}
        <div className="max-w-3xl mx-auto px-4 pb-3 flex items-center gap-1 overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center gap-1 shrink-0">
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${i === step ? "bg-cobalt-light text-cobalt" : i < step ? "text-green-600" : "text-slate-400"}`}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />} {s.label}
              </div>
              {i < STEPS.length - 1 && <div className={`h-px w-3 ${i < step ? "bg-green-400" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {!gateDone ? (
          <LiveVerificationGate caseId={caseId} view={verif} onRefresh={loadVerif} />
        ) : (
        <div key={step} className="cr-fade-up">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-navy flex items-center justify-center"><StepIcon className="w-6 h-6 text-white" /></div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Step {step + 1} of {STEPS.length}</p>
              <h1 className="font-heading text-2xl font-extrabold text-navy">{STEPS[step].label}</h1>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            {step === 0 && (
              <div className="space-y-6">
                <div>
                  <p className="font-heading font-semibold text-navy text-lg">Are you safe right now?</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <button onClick={() => setSafety({ ...safety, safe: true })} data-testid="safe-yes" className={`py-4 rounded-2xl font-medium border transition-all ${safety.safe === true ? "bg-green-50 border-green-300 text-green-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Yes, I'm safe</button>
                    <button onClick={() => setSafety({ ...safety, safe: false })} data-testid="safe-no" className={`py-4 rounded-2xl font-medium border transition-all ${safety.safe === false ? "bg-red-50 border-red-300 text-red-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>No, I need help</button>
                  </div>
                </div>
                <a href="tel:911" data-testid="call-911-step" className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-semibold transition-all active:scale-[0.99]">
                  <Phone className="w-5 h-5" /> Call 911
                </a>
                <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-cobalt mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-navy text-sm">GPS Location</div>
                    {location.lat ? (
                      <div className="text-xs text-slate-500 mt-0.5" data-testid="gps-coords">{location.lat.toFixed(5)}, {location.lng.toFixed(5)} · captured</div>
                    ) : (
                      <button onClick={captureLocation} className="text-xs text-cobalt font-medium mt-0.5">Capture my location</button>
                    )}
                  </div>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <span className="font-medium text-navy">Time:</span> {new Date(caseData.created_at).toLocaleString()}
                </div>

                <div className="pt-2 border-t border-slate-100">
                  <p className="font-heading font-semibold text-navy text-lg">Where was your vehicle damaged? <span className="text-red-500">*</span></p>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="damage-location-options">
                    {DAMAGE_OPTIONS.map((opt) => (
                      <button key={opt} type="button" onClick={() => setDamageLocation(opt)} data-testid={`damage-${opt.toLowerCase().replace(/ /g, "-")}`}
                        className={`py-3 px-2 rounded-xl text-sm font-medium border transition-all ${damageLocation === opt ? "bg-cobalt-light border-cobalt text-cobalt" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="font-heading font-semibold text-navy text-lg">Were you struck by another vehicle?</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setStruckByOther(true)} data-testid="struck-yes" className={`py-3.5 rounded-2xl font-medium border transition-all ${struckByOther === true ? "bg-cobalt-light border-cobalt text-cobalt" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Yes</button>
                    <button type="button" onClick={() => setStruckByOther(false)} data-testid="struck-no" className={`py-3.5 rounded-2xl font-medium border transition-all ${struckByOther === false ? "bg-cobalt-light border-cobalt text-cobalt" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>No</button>
                  </div>
                  {damageLocation === "Rear" && struckByOther === true && (
                    <p className="mt-2 text-xs text-emerald-600 flex items-center gap-1.5" data-testid="rear-end-boost">
                      <BadgeCheck className="w-3.5 h-3.5" /> Rear-impact documented — this strengthens your Crash Ready Score.
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                {savedProfile?.filled > 0 && (
                  <div className="flex justify-end">
                    <button type="button" onClick={applySavedProfile} data-testid="use-saved-profile-chip"
                      className="flex items-center gap-1.5 rounded-full border border-cobalt/30 bg-cobalt-light px-3.5 py-1.5 text-xs font-semibold text-cobalt hover:bg-cobalt hover:text-white transition-colors active:scale-95">
                      <IdCard className="w-3.5 h-3.5" /> Use My Saved Profile
                    </button>
                  </div>
                )}
                {autoFilled && (
                  <div data-testid="autofill-banner" className="rounded-2xl bg-green-50 border border-green-200 p-4 flex items-start gap-3">
                    <BadgeCheck className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">Driver profile auto-filled. Review before continuing.</p>
                      <button type="button" onClick={() => setAutoFilled(false)} data-testid="autofill-edit-btn"
                        className="mt-1 text-xs font-semibold text-green-700 underline underline-offset-2">Edit details below</button>
                    </div>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name" value={driver.name || ""} onChange={(e) => setDriver({ ...driver, name: e.target.value })} placeholder="Your name" data-testid="driver-name" />
                  <Field label="Phone" value={driver.phone || ""} onChange={(e) => setDriver({ ...driver, phone: e.target.value })} placeholder="(555) 555-5555" />
                  <Field label="Email" value={driver.email || ""} onChange={(e) => setDriver({ ...driver, email: e.target.value })} placeholder="you@example.com" />
                  <Field label="Insurance company" value={driver.insurance_company || ""} onChange={(e) => setDriver({ ...driver, insurance_company: e.target.value })} />
                  <Field label="Policy number" value={driver.policy_number || ""} onChange={(e) => setDriver({ ...driver, policy_number: e.target.value })} />
                  <Field label="Driver's license #" value={driver.license || ""} onChange={(e) => setDriver({ ...driver, license: e.target.value })} />
                  <Field label="License plate" value={driver.license_plate || ""} onChange={(e) => setDriver({ ...driver, license_plate: e.target.value })} placeholder="ABC-1234" />
                  <Field label="Vehicle (year/make/model)" value={driver.vehicle || ""} onChange={(e) => setDriver({ ...driver, vehicle: e.target.value })} placeholder="2021 Toyota Camry" />
                </div>
                <label className="flex items-start gap-3 rounded-2xl border border-slate-200 p-4 cursor-pointer hover:bg-slate-50 transition-colors" data-testid="update-profile-toggle">
                  <input type="checkbox" checked={updateProfileToo} onChange={(e) => setUpdateProfileToo(e.target.checked)} className="mt-0.5 h-4 w-4 accent-cobalt" />
                  <span className="text-sm text-slate-600">
                    <span className="font-medium text-navy">Update my Driver Profile too.</span> Save any changes above to my saved profile for future reports.
                  </span>
                </label>
                <div className="rounded-2xl bg-cobalt-light p-4 text-sm text-cobalt flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Tip: Snap photos of your license & insurance card in the Evidence step.
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <label className="text-sm font-medium text-slate-700">Folder</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {FOLDERS.map((f) => (
                      <button key={f} onClick={() => setFolder(f)} data-testid={`folder-${f}`} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${folder === f ? "bg-cobalt text-white border-cobalt" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{f}</button>
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-slate-500 leading-relaxed" data-testid="evidence-guidance">
                    Take pictures of the other driver, their license, their insurance, their car, your car,
                    both cars' damages, the roadway — and anything that can potentially show what happened.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button onClick={() => liveRef.current?.click()} disabled={uploading} data-testid="live-capture-btn" className="border-2 border-cobalt/40 bg-cobalt-light/40 rounded-2xl py-8 flex flex-col items-center gap-2 hover:border-cobalt transition-all">
                    {uploading ? <Loader2 className="w-7 h-7 text-cobalt animate-spin" /> : <Camera className="w-7 h-7 text-cobalt" />}
                    <span className="font-semibold text-navy text-sm">Take live photo / video</span>
                    <span className="text-[11px] text-slate-500 px-3 text-center">Required for verification — captured with the Crash Ready camera</span>
                  </button>
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-evidence-btn" className="border-2 border-dashed border-slate-200 rounded-2xl py-8 flex flex-col items-center gap-2 hover:border-cobalt hover:bg-cobalt-light/40 transition-all">
                    {uploading ? <Loader2 className="w-7 h-7 text-cobalt animate-spin" /> : <Upload className="w-7 h-7 text-slate-400" />}
                    <span className="font-medium text-navy text-sm">Upload from device</span>
                    <span className="text-[11px] text-slate-500 px-3 text-center">Supplemental only — marked as uploaded, not captured live</span>
                  </button>
                </div>
                <p className="text-xs text-slate-400 text-center">Auto-timestamped &amp; geotagged · saved to "{folder}" · new evidence enters temporary encrypted storage until verified</p>
                <input ref={liveRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" onChange={(e) => handleUpload(e, "live")} />
                <input ref={fileRef} type="file" accept="image/*,video/*,audio/*" multiple className="hidden" onChange={(e) => handleUpload(e, "gallery")} />
                {evidence.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {evidence.map((ev) => (
                      <EvidenceThumb key={ev.evidence_id} ev={ev} onRemove={removeEvidence} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                {witnesses.map((w, i) => (
                  <div key={i} className="rounded-2xl border border-slate-100 p-4 space-y-3 relative">
                    <button onClick={() => setWitnesses(witnesses.filter((_, idx) => idx !== i))} className="absolute top-3 right-3 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Name" value={w.name || ""} onChange={(e) => setWitnesses(witnesses.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} />
                      <Field label="Phone" value={w.phone || ""} onChange={(e) => setWitnesses(witnesses.map((x, idx) => idx === i ? { ...x, phone: e.target.value } : x))} />
                      <Field label="Email" value={w.email || ""} onChange={(e) => setWitnesses(witnesses.map((x, idx) => idx === i ? { ...x, email: e.target.value } : x))} />
                      <Field label="Address" value={w.address || ""} onChange={(e) => setWitnesses(witnesses.map((x, idx) => idx === i ? { ...x, address: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Written statement</label>
                      <textarea className={`${inputCls} mt-1.5 min-h-[80px]`} value={w.statement || ""} onChange={(e) => setWitnesses(witnesses.map((x, idx) => idx === i ? { ...x, statement: e.target.value } : x))} />
                    </div>
                  </div>
                ))}
                <button onClick={() => setWitnesses([...witnesses, {}])} data-testid="add-witness" className="flex items-center gap-2 text-cobalt font-medium text-sm"><Plus className="w-4 h-4" /> Add witness</button>
              </div>
            )}

            {step === 4 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Officer name" value={police.officer_name || ""} onChange={(e) => setPolice({ ...police, officer_name: e.target.value })} data-testid="officer-name" />
                <Field label="Badge number" value={police.badge || ""} onChange={(e) => setPolice({ ...police, badge: e.target.value })} />
                <Field label="Agency" value={police.agency || ""} onChange={(e) => setPolice({ ...police, agency: e.target.value })} />
                <Field label="Report number" value={police.report_number || ""} onChange={(e) => setPolice({ ...police, report_number: e.target.value })} />
              </div>
            )}

            {step === 5 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">Log every treatment. This builds your medical timeline.</p>
                {medical.map((m, i) => (
                  <div key={i} className="rounded-2xl border border-slate-100 p-4 space-y-3 relative">
                    <button onClick={() => setMedical(medical.filter((_, idx) => idx !== i))} className="absolute top-3 right-3 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Type</label>
                      <select className={`${inputCls} mt-1.5`} value={m.type || ""} onChange={(e) => setMedical(medical.map((x, idx) => idx === i ? { ...x, type: e.target.value } : x))}>
                        <option value="">Select…</option>
                        {MEDICAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Provider / Facility" value={m.provider || ""} onChange={(e) => setMedical(medical.map((x, idx) => idx === i ? { ...x, provider: e.target.value } : x))} />
                      <Field label="Date" type="date" value={m.date || ""} onChange={(e) => setMedical(medical.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Notes</label>
                      <textarea className={`${inputCls} mt-1.5 min-h-[60px]`} value={m.notes || ""} onChange={(e) => setMedical(medical.map((x, idx) => idx === i ? { ...x, notes: e.target.value } : x))} />
                    </div>
                  </div>
                ))}
                <button onClick={() => setMedical([...medical, {}])} data-testid="add-medical" className="flex items-center gap-2 text-cobalt font-medium text-sm"><Plus className="w-4 h-4" /> Add treatment</button>
              </div>
            )}

            {step === 6 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Claim number" value={insurance.claim_number || ""} onChange={(e) => setInsurance({ ...insurance, claim_number: e.target.value })} data-testid="claim-number" />
                <Field label="Adjuster name" value={insurance.adjuster || ""} onChange={(e) => setInsurance({ ...insurance, adjuster: e.target.value })} />
                <Field label="Adjuster phone" value={insurance.phone || ""} onChange={(e) => setInsurance({ ...insurance, phone: e.target.value })} />
                <Field label="Adjuster email" value={insurance.email || ""} onChange={(e) => setInsurance({ ...insurance, email: e.target.value })} />
                <Field label="Rental car" value={insurance.rental || ""} onChange={(e) => setInsurance({ ...insurance, rental: e.target.value })} />
                <Field label="Total loss status" value={insurance.total_loss || ""} onChange={(e) => setInsurance({ ...insurance, total_loss: e.target.value })} />
                <Field label="Property damage" value={insurance.property_damage || ""} onChange={(e) => setInsurance({ ...insurance, property_damage: e.target.value })} />
                <Field label="Medical payments" value={insurance.medical_payments || ""} onChange={(e) => setInsurance({ ...insurance, medical_payments: e.target.value })} />
              </div>
            )}
          </div>

          {step === STEPS.length - 1 && (
            <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-4" data-testid="attorney-consent-box">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={attorneyConsent} onChange={(e) => setAttorneyConsent(e.target.checked)}
                  data-testid="attorney-consent-checkbox" className="mt-1 h-4 w-4 rounded border-slate-300 text-cobalt focus:ring-cobalt" />
                <span className="text-sm text-slate-600">
                  I authorize Crash Ready to securely submit my accident information to participating attorneys for review.
                  I understand that submitting my information does not guarantee attorney acceptance or create an attorney-client relationship.
                </span>
              </label>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-3">
            <button onClick={() => step > 0 ? setStep(step - 1) : navigate("/dashboard")} className="px-6 py-3 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-all" data-testid="wizard-back">
              {step === 0 ? "Cancel" : "Back"}
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} disabled={saving} className={`${primaryBtn} max-w-[200px]`} data-testid="wizard-next">{saving ? "Saving…" : "Continue"}</button>
            ) : (
              <div className="flex flex-col items-end gap-2">
                <button onClick={() => finish(true)} disabled={saving || !attorneyConsent} className={`${primaryBtn} max-w-[320px] disabled:opacity-40 disabled:cursor-not-allowed`} data-testid="wizard-finish">
                  {saving ? "Submitting…" : "COMPLETE, SAVE & SUBMIT MY CASE"}
                </button>
                <p className="text-[11px] text-slate-400 text-right max-w-[320px]">Your information will be securely saved and submitted for attorney review based on your accident details and verification status.</p>
                <button onClick={() => finish(false)} disabled={saving} data-testid="save-without-submit" className="text-xs text-slate-500 underline hover:text-navy">
                  Save without attorney submission
                </button>
              </div>
            )}
          </div>
        </div>
        )}
      </main>
    </div>
  );
}

function EvidenceThumb({ ev, onRemove }) {
  const token = getToken();
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/evidence/${ev.evidence_id}/download?auth=${token}`;
  return (
    <div className="relative group rounded-xl overflow-hidden border border-slate-100 aspect-square bg-slate-50">
      {ev.storage_state && ev.storage_state !== "PERMANENT" && (
        <span className="absolute top-1.5 left-1.5 z-10 text-[9px] font-bold uppercase tracking-wide bg-amber-400 text-navy px-1.5 py-0.5 rounded-full" data-testid={`temp-badge-${ev.evidence_id}`}>Temp</span>
      )}
      {ev.media_type === "image" ? (
        <img src={url} alt={ev.original_filename} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 text-xs gap-1 p-2">
          <Camera className="w-6 h-6" /> {ev.media_type}
        </div>
      )}
      <button onClick={() => onRemove(ev.evidence_id)} className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Trash2 className="w-3.5 h-3.5 text-red-500" />
      </button>
    </div>
  );
}
